class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isClosing = false;
    this.closeCleanupTimer = null;

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(),
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this),
    );
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add("overflow-hidden", "cart-drawer-open");
  }

  close() {
    if (this.isClosing) return;

    if (!this.classList.contains("active")) {
      document.body.classList.remove("overflow-hidden", "cart-drawer-open");
      return;
    }

    this.isClosing = true;
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);

    const finalizeClose = () => {
      if (!this.isClosing) return;
      this.isClosing = false;
      if (this.closeCleanupTimer) {
        clearTimeout(this.closeCleanupTimer);
        this.closeCleanupTimer = null;
      }
      document.body.classList.remove("overflow-hidden", "cart-drawer-open");
    };

    const drawerInner = this.querySelector(".drawer__inner");
    const transitionTarget = drawerInner || this;
    const onTransitionEnd = (event) => {
      if (event.target !== transitionTarget) return;
      finalizeClose();
    };

    transitionTarget.addEventListener("transitionend", onTransitionEnd, {
      once: true,
    });
    // Fallback for cases where transitionend does not fire.
    this.closeCleanupTimer = setTimeout(finalizeClose, 450);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id,
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open"),
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this),
      );
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

function supportPlanDebugEnabled() {
  try {
    return (
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.localStorage.getItem("hd_support_debug") === "1"
    );
  } catch (error) {
    return false;
  }
}

// Support Plan Selection Handler
class SupportPlanHandler {
  constructor() {
    this.isProcessing = false;
    this.debug = supportPlanDebugEnabled();
    this.init();
  }

  init() {
    this.bindEvents();
    this.interceptCheckout();
    this.clearInitialNoSupportDefaults();
  }

  bindEvents() {
    document.addEventListener("change", (event) => {
      const input = event.target.closest('.support-plan-option input[type="radio"]');
      if (!input) return;
      if (!input.closest(".support-plan-section")) return;

      const option = input.closest(".support-plan-option");
      if (option) {
        this.selectOption(option);
      }
    });
  }

  logDebug(message, context = {}) {
    if (!this.debug) return;
    console.log("[support-plan]", message, context);
  }

  clearInitialNoSupportDefaults() {
    const sections = document.querySelectorAll(".support-plan-section");
    sections.forEach((section) => {
      const existingType = (section.dataset.existingSupportType || "").trim();
      if (existingType) return;

      const checkedInput = section.querySelector(
        '.support-plan-option input[type="radio"]:checked',
      );
      if (!checkedInput) return;

      const checkedOption = checkedInput.closest(".support-plan-option");
      const checkedType = checkedOption?.dataset?.planType || "";
      if (checkedType !== "none") return;

      // If Liquid pre-checked "none" because support_plan_choice="none" is
      // persisted in cart attributes (data-current-plan-type="none"), keep it.
      if ((section.dataset.currentPlanType || "") === "none") return;

      this.setOptionSelection(section, null);
      section.dataset.currentPlanType = "";
      this.logDebug("initial-none-cleared");
    });
  }

  async selectOption(selectedOption) {
    const section = selectedOption.closest(".support-plan-section");
    if (!section) return;

    // Prevent concurrent execution
    if (this.isProcessing) return;

    const previousPlanType = section.dataset.currentPlanType || null;

    const planType = selectedOption.dataset.planType;
    this.setOptionSelection(section, planType);

    const rtdVariantIds = this.getVariantIdsFromDataset(
      section,
      "rtdVariantIds",
      "rtdVariantId",
    );
    const jumpstartVariantIds = this.getVariantIdsFromDataset(
      section,
      "jumpstartVariantIds",
      "jumpstartVariantId",
    );
    const supportVariantIdsInCart = this.getVariantIdsFromDataset(
      section,
      "supportVariantIdsInCart",
      "supportVariantIdInCart",
    );
    const supportLineKeys = this.getStringListFromDataset(
      section,
      "supportLineKeys",
    );
    const supportHandles = this.getStringListFromDataset(
      section,
      "supportHandles",
    ).map((handle) => handle.toLowerCase());
    const supportVariantIds = [
      ...new Set([
        ...rtdVariantIds,
        ...jumpstartVariantIds,
        ...supportVariantIdsInCart,
      ]),
    ];
    const hasSupportInCart =
      supportLineKeys.length > 0 ||
      supportVariantIdsInCart.length > 0 ||
      Boolean((section.dataset.existingSupportType || "").trim());
    this.logDebug("option-selected", {
      planType,
      previousPlanType,
      existingPlanType: section.dataset.existingSupportType || "",
      rtdVariantIds,
      jumpstartVariantIds,
      supportVariantIdsInCart,
      supportLineKeys,
      supportHandles,
      supportVariantIds,
      hasSupportInCart,
    });

    let variantIdToKeep = null;
    if (planType === "rtd" && rtdVariantIds.length > 0) {
      variantIdToKeep = rtdVariantIds[0];
    } else if (
      planType === "jumpstart" &&
      jumpstartVariantIds.length > 0
    ) {
      variantIdToKeep = jumpstartVariantIds[0];
    }

    if (planType === "rtd" && rtdVariantIds.length === 0) {
      this.setOptionSelection(section, previousPlanType);
      console.error("Missing RTD support plan variant id");
      return;
    }

    if (planType === "jumpstart" && jumpstartVariantIds.length === 0) {
      this.setOptionSelection(section, previousPlanType);
      console.error("Missing Jumpstart support plan variant id");
      return;
    }

    // If there is no support line to remove, selecting "none" should not rely on
    // a cart write (which may be rate-limited by external app polling).
    if (planType === "none" && !hasSupportInCart) {
      section.dataset.existingSupportType = "";
      section.dataset.currentPlanType = "none";
      this.logDebug("selection-applied-local-none", {
        planType,
      });
      return;
    }

    this.isProcessing = true;
    this.setCheckoutButtonsDisabled(true);
    try {
      let supportLinesRemoved = false;
      if (planType === "none" && supportLineKeys.length > 0) {
        const cartAfterRemoval =
          await this.removeSupportLineKeys(supportLineKeys);
        supportLinesRemoved = true;
        const noneAppliedAfterRemoval = this.isSupportSelectionApplied(
          cartAfterRemoval,
          "none",
          null,
          supportVariantIds,
          supportHandles,
        );
        this.logDebug("selection-verify-none-after-removal", {
          noneAppliedAfterRemoval,
          supportItems: this.getSupportItemsSnapshot(
            cartAfterRemoval,
            supportVariantIds,
            supportHandles,
          ),
        });
        if (noneAppliedAfterRemoval) {
          // Persist the "none" choice as a cart attribute so that the Liquid
          // re-render can set data-current-plan-type="none" and correctly show
          // the "No support plan" option as selected after re-render.
          const cartWithAttr = await this.updateSupportPlansInCart(
            {},
            { support_plan_choice: "none" },
          );
          section.dataset.existingSupportType = "";
          section.dataset.currentPlanType = "none";
          this.publishCartUpdate(cartWithAttr || cartAfterRemoval);
          this.logDebug("selection-applied-none-remove-only", {
            removedLineKeys: supportLineKeys.length,
          });
          return;
        }
      }

      if (supportLineKeys.length > 0 && !supportLinesRemoved) {
        await this.removeSupportLineKeys(supportLineKeys);
      }

      const variantUpdates = {};
      for (const variantId of supportVariantIds) {
        variantUpdates[variantId] = 0;
      }
      if (variantIdToKeep !== null) {
        variantUpdates[variantIdToKeep] = 1;
      }

      let updatedCart = await this.updateSupportPlansInCart(variantUpdates, {
        support_plan_choice: planType,
      });
      const selectionApplied = this.isSupportSelectionApplied(
        updatedCart,
        planType,
        variantIdToKeep,
        supportVariantIds,
        supportHandles,
      );
      this.logDebug("selection-verify-initial", {
        planType,
        selectionApplied,
        supportItems: this.getSupportItemsSnapshot(
          updatedCart,
          supportVariantIds,
          supportHandles,
        ),
      });

      if (
        !selectionApplied &&
        planType !== "none" &&
        Number.isInteger(variantIdToKeep) &&
        variantIdToKeep > 0
      ) {
        this.logDebug("selection-not-applied-fallback-add", {
          planType,
          variantIdToKeep,
          variantUpdates,
        });
        await this.addSupportVariantToCart(variantIdToKeep);
        updatedCart = await this.updateSupportPlansInCart(variantUpdates, {
          support_plan_choice: planType,
        });
      }

      const selectionAppliedAfterFallback = this.isSupportSelectionApplied(
        updatedCart,
        planType,
        variantIdToKeep,
        supportVariantIds,
        supportHandles,
      );
      this.logDebug("selection-verify-final", {
        planType,
        selectionAppliedAfterFallback,
        supportItems: this.getSupportItemsSnapshot(
          updatedCart,
          supportVariantIds,
          supportHandles,
        ),
      });
      if (!selectionAppliedAfterFallback) {
        throw new Error("Support plan selection did not apply in cart");
      }

      section.dataset.existingSupportType =
        variantIdToKeep === null ? "" : planType;
      section.dataset.currentPlanType = planType;
      this.logDebug("selection-applied", {
        planType,
        variantIdToKeep,
        variantUpdates,
      });
      // Publish once — after the full operation — so the drawer re-renders
      // only when the cart is in its final state.
      this.publishCartUpdate(updatedCart);
    } catch (error) {
      this.setOptionSelection(section, previousPlanType);
      this.logDebug("selection-failed", {
        planType,
        previousPlanType,
        error: String(error),
      });
      console.error("Error updating support plan:", error);
    } finally {
      this.isProcessing = false;
      this.setCheckoutButtonsDisabled(false);
    }
  }

  isSupportSelectionApplied(
    cartData,
    planType,
    variantIdToKeep,
    supportVariantIds,
    supportHandles = [],
  ) {
    if (!cartData || !Array.isArray(cartData.items)) return false;

    const supportIdSet = new Set(
      supportVariantIds.filter((id) => Number.isInteger(id) && id > 0),
    );
    const supportHandleSet = new Set(
      supportHandles
        .map((handle) => String(handle || "").trim().toLowerCase())
        .filter((handle) => handle.length > 0),
    );
    const supportItems = cartData.items.filter(
      (item) => {
        const qty = Number.parseInt(item.quantity, 10);
        if (!(Number.isInteger(qty) && qty > 0)) return false;
        const variantMatch = supportIdSet.has(
          Number.parseInt(item.variant_id, 10),
        );
        const handle = String(item.handle || "").toLowerCase();
        const handleMatch = supportHandleSet.has(handle);
        return variantMatch || handleMatch;
      },
    );

    if (planType === "none") {
      return supportItems.length === 0;
    }

    if (!Number.isInteger(variantIdToKeep) || variantIdToKeep <= 0) {
      return false;
    }

    const keptQty = supportItems.reduce((sum, item) => {
      return Number.parseInt(item.variant_id, 10) === variantIdToKeep
        ? sum + Number.parseInt(item.quantity, 10)
        : sum;
    }, 0);

    if (keptQty < 1) return false;
    return supportItems.every(
      (item) => Number.parseInt(item.variant_id, 10) === variantIdToKeep,
    );
  }

  getSupportItemsSnapshot(cartData, supportVariantIds, supportHandles = []) {
    if (!cartData || !Array.isArray(cartData.items)) return [];

    const supportIdSet = new Set(
      supportVariantIds.filter((id) => Number.isInteger(id) && id > 0),
    );
    const supportHandleSet = new Set(
      supportHandles
        .map((handle) => String(handle || "").trim().toLowerCase())
        .filter((handle) => handle.length > 0),
    );
    return cartData.items
      .filter((item) => {
        const qty = Number.parseInt(item.quantity, 10);
        if (!(Number.isInteger(qty) && qty > 0)) return false;
        const variantMatch = supportIdSet.has(
          Number.parseInt(item.variant_id, 10),
        );
        const handle = String(item.handle || "").toLowerCase();
        const handleMatch = supportHandleSet.has(handle);
        return variantMatch || handleMatch;
      })
      .map((item) => ({
        key: item.key,
        variantId: Number.parseInt(item.variant_id, 10),
        handle: item.handle || "",
        quantity: Number.parseInt(item.quantity, 10) || 0,
        title: item.product_title || item.title || "",
      }));
  }

  setOptionSelection(section, planType) {
    section.querySelectorAll(".support-plan-option").forEach((opt) => {
      opt.classList.remove("selected");
      const input = opt.querySelector('input[type="radio"]');
      if (input) input.checked = false;
    });

    if (!planType) return;

    const nextOption = section.querySelector(
      `.support-plan-option[data-plan-type="${planType}"]`,
    );
    if (!nextOption) return;

    nextOption.classList.add("selected");
    const nextInput = nextOption.querySelector('input[type="radio"]');
    if (nextInput) nextInput.checked = true;
  }

  getVariantIdsFromDataset(section, idsKey, fallbackIdKey) {
    const csvIds = (section.dataset[idsKey] || "")
      .split(",")
      .map((id) => parseInt(id, 10))
      .filter((id) => Number.isInteger(id) && id > 0);

    const fallbackId = parseInt(section.dataset[fallbackIdKey], 10);
    if (
      Number.isInteger(fallbackId) &&
      fallbackId > 0 &&
      !csvIds.includes(fallbackId)
    ) {
      csvIds.push(fallbackId);
    }

    return csvIds;
  }

  getStringListFromDataset(section, key) {
    return (section.dataset[key] || "")
      .split(",")
      .map((value) => value.trim())
      .filter((value) => value.length > 0);
  }

  async updateSupportPlansInCart(updates, attributes = {}) {
    const payload = {};
    if (updates && Object.keys(updates).length > 0) {
      payload.updates = updates;
    }
    if (attributes && Object.keys(attributes).length > 0) {
      payload.attributes = attributes;
    }
    if (Object.keys(payload).length === 0) {
      return null;
    }

    let data = null;
    let lastError = null;
    for (let attempt = 1; attempt <= 3; attempt += 1) {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      try {
        data = await response.json();
      } catch (error) {
        data = null;
      }

      if (response.ok) {
        this.logDebug("cart-update-success", {
          attempt,
          status: response.status,
          payload,
          updates,
          attributes,
        });
        break;
      }

      const errorMessage =
        data?.description || data?.errors || `status ${response.status}`;
      lastError = new Error(`Failed to update support plans: ${errorMessage}`);
      const retryable = response.status === 429 || response.status >= 500;
      this.logDebug("cart-update-failed", {
        attempt,
        status: response.status,
        retryable,
        payload,
        updates,
        attributes,
        errorMessage,
      });
      if (!retryable || attempt === 3) {
        throw lastError;
      }

      await new Promise((resolve) => setTimeout(resolve, 350 * attempt));
    }

    if (!data) {
      throw lastError || new Error("Failed to update support plans: empty response");
    }

    // Caller is responsible for publishing the cart update once the full
    // operation is complete. Publishing here would trigger intermediate
    // re-renders that destroy the checkout button's event listeners.

    return data;
  }

  async addSupportVariantToCart(variantId) {
    const payload = {
      items: [
        {
          id: variantId,
          quantity: 1,
        },
      ],
    };
    let data = null;
    let lastError = null;

    for (let attempt = 1; attempt <= 3; attempt += 1) {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      try {
        data = await response.json();
      } catch (error) {
        data = null;
      }

      if (response.ok) {
        this.logDebug("cart-add-success", {
          attempt,
          status: response.status,
          variantId,
        });
        return data;
      }

      const errorMessage =
        data?.description || data?.errors || `status ${response.status}`;
      lastError = new Error(`Failed to add support plan: ${errorMessage}`);
      const retryable = response.status === 429 || response.status >= 500;
      this.logDebug("cart-add-failed", {
        attempt,
        status: response.status,
        retryable,
        variantId,
        errorMessage,
      });

      if (!retryable || attempt === 3) {
        throw lastError;
      }

      await new Promise((resolve) => setTimeout(resolve, 350 * attempt));
    }

    throw lastError || new Error("Failed to add support plan");
  }

  async removeSupportLineKeys(lineKeys) {
    let latestCart = null;
    for (const lineKey of lineKeys) {
      latestCart = await this.changeCartLineByKey(lineKey, 0);
    }
    return latestCart;
  }

  async changeCartLineByKey(lineKey, quantity) {
    const payload = {
      id: lineKey,
      quantity,
    };
    let data = null;
    let lastError = null;

    for (let attempt = 1; attempt <= 3; attempt += 1) {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      try {
        data = await response.json();
      } catch (error) {
        data = null;
      }

      if (response.ok) {
        this.logDebug("cart-change-success", {
          attempt,
          status: response.status,
          lineKey,
          quantity,
        });
        return data;
      }

      const errorMessage =
        data?.description || data?.errors || `status ${response.status}`;
      lastError = new Error(`Failed to change support line: ${errorMessage}`);
      const retryable = response.status === 429 || response.status >= 500;
      this.logDebug("cart-change-failed", {
        attempt,
        status: response.status,
        retryable,
        lineKey,
        quantity,
        errorMessage,
      });

      if (!retryable || attempt === 3) {
        throw lastError;
      }

      await new Promise((resolve) => setTimeout(resolve, 350 * attempt));
    }

    throw lastError || new Error("Failed to change support line");
  }

  publishCartUpdate(cartData) {
    if (!cartData) return;
    if (
      typeof publish === "function" &&
      typeof PUB_SUB_EVENTS !== "undefined" &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: "support-plan",
        cartData,
      });
    }
  }

  setCheckoutButtonsDisabled(disabled) {
    const checkoutButtons = document.querySelectorAll(
      "#CartDrawer-Checkout, #checkout",
    );
    checkoutButtons.forEach((button) => {
      button.disabled = disabled;
      button.setAttribute("aria-disabled", disabled ? "true" : "false");
    });
  }

  isElementVisible(element) {
    if (!element) return false;
    return Boolean(
      element.offsetWidth ||
        element.offsetHeight ||
        element.getClientRects().length,
    );
  }

  getSupportSectionForCheckoutButton(checkoutBtn) {
    if (!checkoutBtn) return null;

    const drawer = checkoutBtn.closest("cart-drawer");
    if (drawer) {
      const drawerSection = drawer.querySelector("#SupportPlanSection");
      if (drawerSection) return drawerSection;
    }

    const mainCartItems = document.getElementById("main-cart-items");
    if (mainCartItems) {
      const cartPageSection = mainCartItems.querySelector("#SupportPlanSection");
      if (cartPageSection) return cartPageSection;
    }

    const nearestSection = checkoutBtn.closest(".support-plan-section");
    if (nearestSection) return nearestSection;

    const visibleSection = Array.from(
      document.querySelectorAll(".support-plan-section"),
    ).find((section) => this.isElementVisible(section));
    return visibleSection || null;
  }

  interceptCheckout() {
    // Use capture phase to intercept before other handlers
    document.addEventListener(
      "click",
      (event) => {
        const checkoutBtn = event.target.closest(
          "#CartDrawer-Checkout, #checkout",
        );
        if (!checkoutBtn) return;

        if (this.isProcessing) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        this.logDebug("checkout-clicked");
        // Allow checkout regardless of support plan selection state.
      },
      true,
    ); // capture: true
  }

}

function initSupportPlanHandlerOnce() {
  if (window.__hdSupportPlanHandlerInitialized) return;
  window.__hdSupportPlanHandlerInitialized = true;
  window.__hdSupportPlanHandler = new SupportPlanHandler();
  window.hdSupportPlanDebug = {
    enable() {
      try {
        window.localStorage.setItem("hd_support_debug", "1");
      } catch (error) {}
      window.__hdSupportPlanHandler.debug = true;
      console.info("[support-plan] debug enabled");
    },
    disable() {
      try {
        window.localStorage.removeItem("hd_support_debug");
      } catch (error) {}
      window.__hdSupportPlanHandler.debug = false;
      console.info("[support-plan] debug disabled");
    },
    dump() {
      return {
        enabled: Boolean(window.__hdSupportPlanHandler?.debug),
      };
    },
  };
}

// Initialize support plan handler when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    initSupportPlanHandlerOnce();
  });
} else {
  initSupportPlanHandlerOnce();
}